import { createA11ySdk } from '../../../utils/velo/a11ySdk';
import { createLinkSdkFactory } from '../../../utils/velo/LinkSdk';
import {
  assert,
  reportError,
  parseMediaSrc,
  createMediaSrc,
} from '@wix/editor-elements-corvid-utils';
import { composeSDKFactories } from '../../../utils/velo/composeSdks';
import { DEFAULT_LOGO_SIZE } from './constants';
import type { CorvidSDKFactory } from '@wix/editor-elements-types/corvid';
import type { LogoProps } from './Logo.types';

export const createLogoSdk: CorvidSDKFactory<LogoProps> = api => {
  const { props, setProps } = api;
  return {
    get src() {
      const { image } = props;
      return (
        createMediaSrc({
          type: 'image',
          mediaId: image?.uri || '',
          title: image?.name,
          width: image?.width,
          height: image?.height,
        }).item ?? ''
      );
    },
    set src(value: string) {
      const src = assert.isNil(value) ? '' : value;
      const {
        height,
        width,
        title: name,
        error,
        mediaId: uri,
      } = parseMediaSrc(src.toString(), 'image');

      if (error) {
        reportError(
          `The "src" property cannot be set to "${src}". It must be a valid URL starting with "http://", "https://", or "wix:image://".`,
        );
        return;
      }

      const { image } = props;
      const currentImageData = image;

      const updatedImageData = {
        ...currentImageData,
        width: width || currentImageData?.width || DEFAULT_LOGO_SIZE,
        height: height || currentImageData?.height || DEFAULT_LOGO_SIZE,
        uri: uri || currentImageData?.uri || '',
        name: name || currentImageData?.name || 'logo',
      };

      setProps({ image: updatedImageData });
    },
    get alt() {
      return props.a11y?.ariaLabel || '';
    },
    set alt(value: string) {
      setProps({
        a11y: { ...props.a11y, ariaLabel: assert.isNil(value) ? '' : value },
      });
    },
    get name() {
      return props.image?.name || '';
    },
    set name(value: string) {
      const currentImage = props?.image;
      const updatedImage = {
        ...currentImage,
        name: assert.isNil(value) ? '' : value,
      };
      setProps({ image: updatedImage });
    },
    get width() {
      return props.image?.width || DEFAULT_LOGO_SIZE;
    },
    set width(value: number) {
      const currentImage = props?.image;
      const updatedImage = {
        ...currentImage,
        width:
          typeof value === 'number' && value > 0 ? value : DEFAULT_LOGO_SIZE,
      };
      setProps({ image: updatedImage });
    },
    get height() {
      return props.image?.height || DEFAULT_LOGO_SIZE;
    },
    set height(value: number) {
      const currentImage = props?.image;
      const updatedImage = {
        ...currentImage,
        height:
          typeof value === 'number' && value > 0 ? value : DEFAULT_LOGO_SIZE,
      };
      setProps({ image: updatedImage });
    },
    get type() {
      return '$w.Logo';
    },
    toJSON() {
      const image = props.image;
      return {
        type: '$w.Logo',
        src: this.src,
        image: {
          uri: image?.uri || '',
          width: image?.width || DEFAULT_LOGO_SIZE,
          height: image?.height || DEFAULT_LOGO_SIZE,
          name: image?.name || '',
        },
        a11y: { ariaLabel: this.alt },
        name: image?.name || '',
        width: image?.width || DEFAULT_LOGO_SIZE,
        height: image?.height || DEFAULT_LOGO_SIZE,
      };
    },
  };
};

export default composeSDKFactories([
  createLogoSdk,
  createLinkSdkFactory({ linkProperty: 'link' }),
  createA11ySdk({ a11yProperty: 'a11y' }),
]);
